// contract.ts

import { initContract } from '@ts-rest/core'
import { z } from 'zod'

const c = initContract()

const SingleEndUserSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  middle: z.string().nullable().optional(), // Assuming middle name can be null or undefined
  lastName: z.string().nullable(),
  email: z.string().nullable().optional(),
  address: z.any().optional(),
  customer: z.object({
    id: z.string(),
    name: z.string(),
  }),
  endUserCampaigns: z.array(
    z
      .object({
        id: z.string(),
        firstName: z.string().nullable().optional(), // Assuming these can be null
        middle: z.string().nullable().optional(),
        lastName: z.string().nullable().optional(),
        email: z.string().nullable().optional(),
        address: z.any().optional(),
        questionnaireAnswers: z.any().optional(), // Assuming this can be optional
        happinessAnswers: z.any().optional(),
        incentive: z
          .object({
            incentiveItem: z.object({
              name: z.string(),
            }),
          })
          .nullable()
          .optional(),
        birthday: z.string().nullable().optional(),
        phoneNumber: z.string().nullable().optional(),
        digitalCode: z.string().nullable().optional(),
        privacyLinkAccepted: z.date().nullable(),
        allowContactConsent: z.boolean().nullable(),
        optionalData: z.any(),
        mapping: z.any(),
        incentiveItemVoucherCodes: z
          .array(
            z.object({
              code: z.string().nullable().optional(),
              details: z.any().nullable().optional(),
              isUsed: z.boolean().nullable().optional(),
              selectedAt: z.date().nullable().optional(),
              status: z.string().nullable().optional(),
            }),
          )
          .optional(),
        uniqueCode: z.string().optional(), // Assuming this can be optional
        campaign: z.object({
          id: z.string(),
          name: z.string(),
        }),
        events: z
          .array(
            z.object({
              id: z.string(),
              action: z.string(),
              data: z.any(),
              createdAt: z.date(),
            }),
          )
          .optional(),
      })
      .optional(),
  ),
})

const EndUserListSchema = z.array(SingleEndUserSchema)

const prefix = '/v2'
export const endUser = c.router({
  get: {
    method: 'GET',
    path: `${prefix}/end-users-list`,
    query: z.object({
      // Add query parameters definition
      customerId: z.string().optional(),
      search: z.string().optional(),
      sort: z
        .object({
          sortBy: z.string().optional(),
          sortOrder: z.enum(['asc', 'desc']).optional(),
        })
        .optional(),
      pagination: z
        .object({
          page: z
            .union([
              z.number(),
              z
                .string()
                .regex(/^[0-9]+$/, {
                  message: 'Page must be alphanumeric',
                })
                .transform((val) => parseInt(val, 10)),
            ])
            .optional(),
          perPage: z
            .union([
              z.number(),
              z
                .string()
                .regex(/^[0-9]+$/, {
                  message: 'Page must be alphanumeric',
                })
                .transform((val) => parseInt(val, 10)),
            ])
            .optional(),
        })
        .optional(),
    }),
    responses: {
      200: EndUserListSchema, // Updated to use the new list schema name
    },
    summary:
      'Get a list of end users with optional filtering, pagination, and sorting',
  },
  getById: {
    // New endpoint definition
    method: 'GET',
    path: `${prefix}/end-users-list/:id`,
    responses: {
      200: SingleEndUserSchema,
      404: z.object({ message: z.string() }), // For user not found
    },
    pathParams: z.object({
      // Define path parameter
      id: z.string(),
    }),
    summary: 'Get a single end user by ID',
  },
})
